/**
 * Hooks for the admin human review queue.
 *
 * @module useAdminHumanReviews
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  claimReview,
  completeReview,
  getAdminHumanReviews,
  getAdminReviewDetail,
  type AdminReviewDetail,
  type AdminReviewFilters,
  type AdminReviewListResponse,
  type CompleteReviewPayload,
} from '@/lib/adminHumanReviewApi';

export const ADMIN_REVIEW_LIST_KEY = ['adminHumanReviews'] as const;
export const ADMIN_REVIEW_DETAIL_KEY = ['adminHumanReviewDetail'] as const;

export function useAdminHumanReviews(filters: AdminReviewFilters) {
  const { data, isLoading, isError, refetch } = useQuery<AdminReviewListResponse | null>({
    queryKey: [...ADMIN_REVIEW_LIST_KEY, filters],
    queryFn: () => getAdminHumanReviews(filters),
    staleTime: 0,
  });
  return { data: data ?? null, isLoading, isError, refetch };
}

export function useAdminReviewDetail(id: string | null) {
  const { data, isLoading, isError, refetch } = useQuery<AdminReviewDetail | null>({
    queryKey: [...ADMIN_REVIEW_DETAIL_KEY, id],
    queryFn: () => (id ? getAdminReviewDetail(id) : Promise.resolve(null)),
    enabled: Boolean(id),
    staleTime: 0,
  });
  return { detail: data ?? null, isLoading, isError, refetch };
}

export function useClaimReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => claimReview(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_REVIEW_LIST_KEY });
      void queryClient.invalidateQueries({ queryKey: ADMIN_REVIEW_DETAIL_KEY });
    },
  });
}

export function useCompleteReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CompleteReviewPayload }) =>
      completeReview(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_REVIEW_LIST_KEY });
      void queryClient.invalidateQueries({ queryKey: ADMIN_REVIEW_DETAIL_KEY });
    },
  });
}
